import React, { createContext, useContext, useEffect, useState } from 'react';

// Lofi radio stations
const RADIO_STATIONS = [
  {
    id: 'lofi-girl',
    name: 'Lofi Girl',
    url: 'https://play.streamafrica.net/lofiradio',
  },
  {
    id: 'lofi-hip-hop',
    name: 'Lofi Hip Hop',
    url: 'http://usa9.fastcast4u.com/proxy/jamz?mp=/1',
  },
  {
    id: 'the-bootleg-boy',
    name: 'The Bootleg Boy',
    url: 'http://stream.zeno.fm/0r0xa792kwzuv',
  },
];

// Radio station type
interface RadioStation {
  id: string;
  name: string;
  url: string;
  isPlaying: boolean;
  volume: number;
  audio: HTMLAudioElement | null;
}

interface AudioContextType {
  radioStations: RadioStation[];
  activeRadioStation: string | null;
  volume: number;
  setVolume: (volume: number) => void;
  playRadioStation: (stationId: string) => void;
  stopRadio: () => void;
  showRadioPanel: boolean;
  toggleRadioPanel: () => void;
}

// Create context for audio controls
const AudioContext = createContext<AudioContextType | null>(null);

// Audio provider component
export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [volume, setVolume] = useState(0.7);
  const [activeRadioStation, setActiveRadioStation] = useState<string | null>(
    null
  );
  const [showRadioPanel, setShowRadioPanel] = useState(false);
  const [radioStations, setRadioStations] = useState<RadioStation[]>(
    RADIO_STATIONS.map((station) => ({
      ...station,
      isPlaying: false,
      volume: volume,
      audio: null,
    }))
  );

  // Initialize radio stations when component mounts
  useEffect(() => {
    // Initialize radio station audio elements
    const updatedRadioStations = [...radioStations];
    updatedRadioStations.forEach((station, index) => {
      const audio = new Audio();
      audio.volume = volume;
      // Don't set source yet to prevent auto-buffering of all stations

      updatedRadioStations[index] = {
        ...station,
        audio,
      };
    });
    setRadioStations(updatedRadioStations);

    // Cleanup on unmount
    return () => {
      updatedRadioStations.forEach((station) => {
        if (station.audio) {
          station.audio.pause();
          station.audio.src = '';
        }
      });
    };
  }, []);

  // Update volume when it changes
  useEffect(() => {
    radioStations.forEach((station) => {
      if (station.audio) {
        station.audio.volume = volume;
      }
    });
  }, [volume, radioStations]);

  // Add keyboard shortcuts for audio control
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Number keys 3-6 to play specific radio stations
      if (e.key === '3' && !e.repeat && RADIO_STATIONS[0]) {
        playRadioStation(RADIO_STATIONS[0].id);
      }
      if (e.key === '4' && !e.repeat && RADIO_STATIONS[1]) {
        playRadioStation(RADIO_STATIONS[1].id);
      }
      if (e.key === '5' && !e.repeat && RADIO_STATIONS[2]) {
        playRadioStation(RADIO_STATIONS[2].id);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [volume, activeRadioStation]);

  // Play specific radio station
  const playRadioStation = (stationId: string) => {
    // First stop any currently playing radio
    stopRadio();

    // Find the station and start playing it
    setRadioStations((prev) => {
      const updatedStations = [...prev];
      const stationIndex = updatedStations.findIndex((s) => s.id === stationId);

      if (stationIndex >= 0) {
        const station = updatedStations[stationIndex];

        if (station.audio) {
          // Set the source only when we want to play it to prevent unnecessary buffering
          station.audio.src = station.url;
          station.audio.volume = volume;
          station.audio.play().catch((error) => {
            console.warn(`Radio playback failed for ${station.name}:`, error);
            // If playing fails, reset active station
            setActiveRadioStation(null);
          });

          updatedStations[stationIndex] = {
            ...station,
            isPlaying: true,
          };

          setActiveRadioStation(stationId);
        }
      }

      return updatedStations;
    });
  };

  // Stop all radio stations
  const stopRadio = () => {
    setRadioStations((prev) => {
      const updatedStations = [...prev];

      updatedStations.forEach((station, index) => {
        if (station.audio) {
          station.audio.pause();
          station.audio.src = ''; // Clear source to stop buffering

          updatedStations[index] = {
            ...station,
            isPlaying: false,
          };
        }
      });

      setActiveRadioStation(null);
      return updatedStations;
    });
  };

  const toggleRadioPanel = () => {
    setShowRadioPanel(!showRadioPanel);
  };

  const value = {
    radioStations,
    activeRadioStation,
    volume,
    setVolume,
    playRadioStation,
    stopRadio,
    showRadioPanel,
    toggleRadioPanel,
  };

  return (
    <AudioContext.Provider value={value}>{children}</AudioContext.Provider>
  );
};

// Hook to use audio context
export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};

// Audio controls UI component
export const AudioControls: React.FC = () => {
  const {
    radioStations,
    activeRadioStation,
    volume,
    setVolume,
    playRadioStation,
    stopRadio,
    showRadioPanel,
    toggleRadioPanel,
  } = useAudio();

  const buttonContainerStyle: React.CSSProperties = {
    position: 'absolute',
    top: '10px',
    right: '10px',
    //display: 'flex',
    gap: '10px',
    zIndex: 1000,
    display: 'none',
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    color: 'white',
    padding: '5px 10px',
    borderRadius: '5px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  };

  const panelStyle: React.CSSProperties = {
    position: 'absolute',
    top: '45px',
    right: '10px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    padding: '10px',
    borderRadius: '5px',
    zIndex: 1000,
    display: showRadioPanel ? 'flex' : 'none',
    flexDirection: 'column',
    gap: '10px',
    width: '200px',
  };

  const radioButtonStyle: React.CSSProperties = {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    border: 'none',
    borderRadius: '3px',
    color: 'white',
    padding: '5px 10px',
    cursor: 'pointer',
    fontSize: '12px',
  };

  const activeRadioButtonStyle: React.CSSProperties = {
    ...radioButtonStyle,
    backgroundColor: 'rgba(140, 70, 200, 0.9)',
  };

  return (
    <>
      <div style={buttonContainerStyle}>
        {/* Radio button */}
        <button
          style={{
            ...buttonStyle,
          }}
          onClick={toggleRadioPanel}
        >
          <span role="img" aria-label="radio" style={{ fontSize: '16px' }}>
            📻
          </span>
        </button>
      </div>

      {/* Radio control panel */}
      <div style={panelStyle}>
        <div
          style={{
            fontSize: '12px',
            padding: '5px',
            borderRadius: '3px',
            marginBottom: '5px',
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
            Radio Stations
          </div>
        </div>

        {/* Volume control */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            marginBottom: '5px',
          }}
        >
          <span style={{ fontSize: '12px' }}>Volume:</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            style={{ flexGrow: 1 }}
          />
        </div>

        {/* Radio station buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {radioStations.map((station, index) => (
            <button
              key={station.id}
              style={
                activeRadioStation === station.id
                  ? activeRadioButtonStyle
                  : radioButtonStyle
              }
              onClick={() =>
                activeRadioStation === station.id
                  ? stopRadio()
                  : playRadioStation(station.id)
              }
            >
              {index + 3}. {station.name}{' '}
              {activeRadioStation === station.id ? '▶️' : ''}
            </button>
          ))}
        </div>

        {/* Stop radio button */}
        <button style={radioButtonStyle} onClick={stopRadio}>
          Stop Radio
        </button>
      </div>
    </>
  );
};
